
import Sticky from "components/layout/Sticky.vue";
import themes from "data/themes";
import { CoercableComponent, StyleValue, Visibility } from "features/feature";
import { GenericTab } from "features/tabs/tab";
import TabButton from "features/tabs/TabButton.vue";
import { GenericTabButton } from "features/tabs/tabFamily";
import settings from "game/settings";
import { coerceComponent, isCoercableComponent, processedPropType, unwrapRef } from "util/vue";
import {
    Component,
    computed,
    defineComponent,
    PropType,
    Ref,
    shallowRef,
    toRefs,
    unref,
    watchEffect
} from "vue";

export default defineComponent({
    props: {
        visibility: {
            type: processedPropType<Visibility>(Number),
            required: true
        },
        activeTab: {
            type: processedPropType<GenericTab | CoercableComponent | null>(Object),
            required: true
        },
        selected: {
            type: Object as PropType<Ref<string>>,
            required: true
        },
        tabs: {
            type: processedPropType<Record<string, GenericTabButton>>(Object),
            required: true
        },
        style: processedPropType<StyleValue>(String, Object, Array),
        classes: processedPropType<Record<string, boolean>>(Object)
    },
    components: {
        Sticky,
        TabButton
    },
    setup(props) {
        const { activeTab } = toRefs(props);

        const floating = computed(() => {
            return themes[settings.theme].floatingTabs;
        });

        const component = shallowRef<Component | string>("");

        watchEffect(() => {
            const currActiveTab = unwrapRef(activeTab);
            if (currActiveTab == null) {
                component.value = "";
                return;
            }
            if (isCoercableComponent(currActiveTab)) {
                component.value = coerceComponent(currActiveTab);
                return;
            }
            component.value = coerceComponent(unref(currActiveTab.display));
        });

        const tabClasses = computed(() => {
            const currActiveTab = unwrapRef(activeTab);
            const tabClasses =
                isCoercableComponent(currActiveTab) || !currActiveTab
                    ? undefined
                    : unref(currActiveTab.classes);
            return tabClasses;
        });

        const tabStyle = computed(() => {
            const currActiveTab = unwrapRef(activeTab);
            return isCoercableComponent(currActiveTab) || !currActiveTab
                ? undefined
                : unref(currActiveTab.style);
        });

        function gatherButtonProps(button: GenericTabButton) {
            const { display, style, classes, glowColor, visibility } = button;
            return { display, style: unref(style), classes, glowColor, visibility };
        }

        return {
            floating,
            tabClasses,
            tabStyle,
            Visibility,
            component,
            gatherButtonProps,
            unref
        };
    }
});
